"use client";

import { Suspense, useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { VideoGrid } from "@/components/video/VideoGrid";
import { SearchBar } from "@/components/search/SearchBar";
import { useTranslation } from "@/hooks/useTranslation";
import { useLanguageStore } from "@/stores/language";
import type { TranslationKey } from "@/lib/i18n";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { WelcomeToast } from "@/components/WelcomeToast";
import type { VideoResponse } from "@/types/api";

interface BrowseClientProps {
  myVideos: VideoResponse[];
  allVideos: VideoResponse[];
  allVideosTotal: number;
  pageSize: number;
  isLoggedIn: boolean;
  canSeeAll: boolean;
  showWelcome?: boolean;
}

type Tab = "mine" | "all";

const POLL_INTERVAL_MS = 5000;
const TERMINAL_STATUSES = new Set(["COMPLETED", "FAILED"]);

// Three-step editorial spread shown to anonymous visitors. Each step pairs
// a real dish photo (in /public/landing/) with a hand-lettered caption.
type StepVisual = {
  n: string;
  numeralKey: TranslationKey;
  titleKey: TranslationKey;
  bodyKey: TranslationKey;
  img: { src: string; altKey: TranslationKey };
  sticker: { textKey: TranslationKey; rotate: string };
};

const HOW_IT_WORKS_STEPS: StepVisual[] = [
  {
    n: "01",
    numeralKey: "landing.step1.numeral",
    titleKey: "landing.step1.title",
    bodyKey: "landing.step1.body",
    img: { src: "/landing/hong-shao-rou.png", altKey: "landing.step1.alt" },
    sticker: { textKey: "landing.step1.sticker", rotate: "-rotate-3" },
  },
  {
    n: "02",
    numeralKey: "landing.step2.numeral",
    titleKey: "landing.step2.title",
    bodyKey: "landing.step2.body",
    img: { src: "/landing/white-cut-chicken.png", altKey: "landing.step2.alt" },
    sticker: { textKey: "landing.step2.sticker", rotate: "rotate-2" },
  },
  {
    n: "03",
    numeralKey: "landing.step3.numeral",
    titleKey: "landing.step3.title",
    bodyKey: "landing.step3.body",
    img: { src: "/landing/squirrel-fish.png", altKey: "landing.step3.alt" },
    sticker: { textKey: "landing.step3.sticker", rotate: "-rotate-2" },
  },
];

export function BrowseClient({
  myVideos,
  allVideos,
  allVideosTotal,
  pageSize,
  isLoggedIn,
  canSeeAll,
  showWelcome = false,
}: BrowseClientProps) {
  const { t } = useTranslation();
  const lang = useLanguageStore((s) => s.language);
  const [tab, setTab] = useState<Tab>("mine");
  // myVideos is owned as state so individual cards can be patched in place
  // by the per-card poll below — avoids a full router.refresh().
  const [myList, setMyList] = useState(myVideos);
  const [allList, setAllList] = useState(allVideos);
  const [allPage, setAllPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Re-sync from server props when the page navigates (e.g. user submitted
  // another video and routed back here, or admin's All tab refreshed).
  useEffect(() => {
    setMyList(myVideos);
  }, [myVideos]);

  const processingIds = myList
    .filter((v) => !TERMINAL_STATUSES.has(v.status))
    .map((v) => v.id);

  // Stable cache key so the effect only re-arms when the SET of in-flight
  // ids changes (not when their progress %s change inside myList).
  const processingKey = processingIds.join(",");

  useEffect(() => {
    if (!processingKey) return;
    const ids = processingKey.split(",");
    let cancelled = false;

    const tick = async () => {
      const updates = await Promise.all(
        ids.map(async (id) => {
          try {
            const res = await fetch(`/api/v1/videos/${id}`, { cache: "no-store" });
            if (!res.ok) return null;
            const data = await res.json();
            return data?.success ? (data.data as VideoResponse) : null;
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      const byId = new Map<string, VideoResponse>();
      for (const u of updates) if (u) byId.set(u.id, u);
      if (byId.size === 0) return;
      setMyList((prev) => prev.map((v) => byId.get(v.id) ?? v));
    };

    // Immediate tick so newly-submitted cards don't sit at 0% for 5s.
    tick();
    const handle = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [processingKey]);

  const allHasMore = canSeeAll && tab === "all" && allList.length < allVideosTotal;

  const loadMoreAll = useCallback(async () => {
    if (loading || !allHasMore) return;
    setLoading(true);
    try {
      const nextPage = allPage + 1;
      const res = await fetch(
        `/api/v1/videos?status=COMPLETED&sortBy=createdAt&sortOrder=desc&page=${nextPage}&limit=${pageSize}`
      );
      const data = await res.json();
      if (data.success && data.data.videos.length > 0) {
        setAllList((prev) => [...prev, ...data.data.videos]);
        setAllPage(nextPage);
      }
    } catch {
      // Mobile networks drop requests mid-scroll; the sentinel stays mounted
      // so the next scroll retries. Without this, the rejection escapes the
      // IntersectionObserver callback as an unhandled rejection.
    } finally {
      setLoading(false);
    }
  }, [loading, allHasMore, allPage, pageSize]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreAll();
      },
      { rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMoreAll]);

  if (!isLoggedIn) {
    const steps = HOW_IT_WORKS_STEPS;
    return (
      <div className="landing-paper -mx-4 -mt-6 -mb-6 overflow-hidden">
        {/* ── Masthead ───────────────────────────────────────── */}
        <header className="mx-auto max-w-xl px-5 pt-8 pb-4">
          <div className="flex items-baseline justify-between border-b border-[color:var(--color-foreground)]/15 pb-3 font-mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--color-foreground)]/55">
            <span>Nº 01</span>
            <span className="hidden sm:inline">For the family</span>
            <span>Spring 2026</span>
          </div>
        </header>

        {/* ── Hero ───────────────────────────────────────────── */}
        <section className="relative mx-auto max-w-xl px-5 pb-16 pt-6">
          <p className="landing-kicker font-mono text-[10.5px] uppercase tracking-[0.3em] text-[color:var(--color-primary)]">
            {t("landing.kicker")}
          </p>
          <h1 className="landing-display mt-5 text-[clamp(40px,10vw,64px)] font-light leading-[0.95] tracking-tight text-[color:var(--color-foreground)]">
            {lang === "en" ? (
              <>
                Home,
                <br />
                <span className="italic text-[color:var(--color-primary)]">cooked.</span>
              </>
            ) : (
              <>
                家的味道，
                <br />
                <span className="italic text-[color:var(--color-primary)]">每一天</span>
                。
              </>
            )}
          </h1>
          <p className="mt-6 max-w-[28ch] text-[15.5px] leading-[1.75] text-[color:var(--color-foreground)]/75">
            {t("landing.pitch")}
          </p>

          {/* Collage: three polaroid-style photo chips with a hand-written garnish */}
          <div className="relative mt-10 h-48">
            <div className="absolute left-0 top-0 h-32 w-32 -rotate-3 bg-white p-1.5 shadow-[0_8px_24px_-10px_rgba(26,22,19,0.45)]">
              <div className="relative h-full w-full overflow-hidden">
                <Image
                  src="/landing/boiled-fish-chili.png"
                  alt={t("landing.altBoiledFish")}
                  fill
                  sizes="128px"
                  className="object-cover"
                  priority
                />
              </div>
            </div>
            <div className="absolute left-28 top-10 h-28 w-28 rotate-[5deg] bg-white p-1.5 shadow-[0_8px_24px_-10px_rgba(26,22,19,0.45)]">
              <div className="relative h-full w-full overflow-hidden">
                <Image
                  src="/landing/mapo-tofu.png"
                  alt={t("landing.altMapoTofu")}
                  fill
                  sizes="112px"
                  className="object-cover"
                  priority
                />
              </div>
            </div>
            <div className="absolute right-0 top-0 h-36 w-32 -rotate-[6deg] bg-white p-1.5 shadow-[0_8px_24px_-10px_rgba(26,22,19,0.45)]">
              <div className="relative h-full w-full overflow-hidden">
                <Image
                  src="/landing/steamed-buns.png"
                  alt={t("landing.altSteamedBuns")}
                  fill
                  sizes="128px"
                  className="object-cover"
                  priority
                />
              </div>
            </div>
            <span
              className="landing-hand absolute -top-1 right-6 rotate-[8deg] text-3xl text-[color:var(--color-primary)]"
              aria-hidden
            >
              {t("landing.yum")}
            </span>
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-6">
            <Link
              href="/login"
              className="group inline-flex items-center gap-3 bg-[color:var(--color-foreground)] px-7 py-3.5 text-[14px] font-medium tracking-wide text-[color:var(--color-background)] transition hover:opacity-90"
            >
              {t("landing.getStarted")}
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </Link>
            <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[color:var(--color-foreground)]/55">
              {t("landing.freeHint")}
            </span>
          </div>
        </section>

        {/* ── Section rule ──────────────────────────────────── */}
        <div className="mx-auto max-w-xl px-5">
          <div className="flex items-center gap-4 py-2">
            <span className="h-px flex-1 bg-[color:var(--color-foreground)]/15" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--color-foreground)]/50">
              {t("landing.howItWorks")}
            </span>
            <span className="h-px flex-1 bg-[color:var(--color-foreground)]/15" />
          </div>
        </div>

        {/* ── Three editorial spreads ───────────────────────── */}
        <section className="mx-auto max-w-xl px-5 pb-14 pt-8">
          <ol className="space-y-12">
            {steps.map((step, i) => {
              const reversed = i % 2 === 1;
              return (
                <li
                  key={step.n}
                  className={`relative grid grid-cols-12 items-start gap-4 ${
                    reversed ? "" : ""
                  }`}
                >
                  {/* Giant numeral watermark */}
                  <span
                    aria-hidden
                    className={`landing-display pointer-events-none absolute top-0 select-none text-[120px] font-light leading-none text-[color:var(--color-primary)]/10 ${
                      reversed ? "right-0" : "left-0"
                    }`}
                  >
                    {t(step.numeralKey)}
                  </span>

                  {/* Image chip — polaroid style with a real photo inside. */}
                  <div
                    className={`col-span-6 ${
                      reversed ? "col-start-7 order-2" : "col-start-1"
                    }`}
                  >
                    <div
                      className={`relative aspect-[4/5] w-full bg-white p-1.5 shadow-[0_10px_30px_-14px_rgba(26,22,19,0.45)] ${
                        reversed ? "rotate-[2deg]" : "-rotate-[2deg]"
                      }`}
                    >
                      <div className="relative h-full w-full overflow-hidden">
                        <Image
                          src={step.img.src}
                          alt={t(step.img.altKey)}
                          fill
                          sizes="(max-width: 640px) 45vw, 280px"
                          className="object-cover"
                        />
                      </div>
                    </div>
                    <span
                      className={`landing-hand mt-2 block text-center text-lg text-[color:var(--color-primary)] ${step.sticker.rotate}`}
                      aria-hidden
                    >
                      {t(step.sticker.textKey)}
                    </span>
                  </div>

                  {/* Copy */}
                  <div
                    className={`col-span-6 relative z-10 ${
                      reversed ? "col-start-1 order-1 pr-2" : "col-start-7 pl-2"
                    }`}
                  >
                    <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[color:var(--color-primary)]">
                      Step {step.n}
                    </div>
                    <h2 className="landing-display mt-2 text-[28px] font-light leading-[1.05] tracking-tight text-[color:var(--color-foreground)]">
                      {t(step.titleKey)}
                    </h2>
                    <p className="mt-3 text-[13.5px] leading-[1.7] text-[color:var(--color-foreground)]/70">
                      {t(step.bodyKey)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        {/* ── Closing note ──────────────────────────────────── */}
        <section className="mx-auto max-w-xl px-5 pb-20 pt-6">
          <div className="rounded-sm border-t border-b border-[color:var(--color-foreground)]/15 py-8 text-center">
            <p className="landing-display mx-auto max-w-[22ch] text-[26px] font-light italic leading-[1.25] text-[color:var(--color-foreground)]">
              {t("landing.dinnerTonight")}
            </p>
            <Link
              href="/login"
              className="mt-6 inline-flex items-center gap-2 border-b border-[color:var(--color-foreground)] pb-0.5 text-[14px] font-medium tracking-wide text-[color:var(--color-foreground)] transition hover:text-[color:var(--color-primary)] hover:border-[color:var(--color-primary)]"
            >
              {t("landing.startCooking")}
              <span>→</span>
            </Link>
          </div>
          <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--color-foreground)]/45">
            {t("landing.tagline")}
          </p>
        </section>
      </div>
    );
  }

  const showTabs = canSeeAll;
  const displayVideos = tab === "all" ? allList : myList;
  const myEmpty = myList.length === 0;

  return (
    <div className="space-y-6">
      {showWelcome && <WelcomeToast />}
      <div className="flex flex-col items-center gap-4">
        <h1 className="text-2xl font-bold">{t("browse.title")}</h1>
        <Suspense>
          <SearchBar />
        </Suspense>
      </div>

      <div className="space-y-4">
        {showTabs ? (
          <div className="flex gap-4 border-b border-border">
            <button
              onClick={() => setTab("mine")}
              className={cn(
                "pb-2 text-lg font-semibold transition-colors",
                tab === "mine"
                  ? "border-b-2 border-primary text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {t("browse.myVideos")}
            </button>
            <button
              onClick={() => setTab("all")}
              className={cn(
                "pb-2 text-lg font-semibold transition-colors",
                tab === "all"
                  ? "border-b-2 border-primary text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {t("browse.allVideos")}
            </button>
          </div>
        ) : (
          <h2 className="text-lg font-semibold">
            {t("browse.myVideos")}
          </h2>
        )}

        {tab === "mine" && myEmpty ? (
          <div className="space-y-3 py-12 text-center">
            <p className="text-muted-foreground">
              {t("browse.noneYet")}
            </p>
            <Link href="/submit">
              <Button>
                {t("browse.addFirst")}
              </Button>
            </Link>
          </div>
        ) : (
          <VideoGrid videos={displayVideos} />
        )}

        {tab === "all" && allHasMore && (
          <div ref={sentinelRef} className="flex justify-center py-4">
            {loading && (
              <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {Array.from({ length: pageSize }).map((_, i) => (
                  <div key={i} className="space-y-3">
                    <Skeleton className="aspect-[9/16] rounded-xl" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-3 w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "all" && !allHasMore && allList.length > 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t("browse.noMore")}
          </p>
        )}
      </div>
    </div>
  );
}
